/**
 * What a map is supposed to look like, decided rather than derived.
 *
 * The old theme system gave every map its own sky, fog, sun tint and hemisphere
 * colours — a sunset orange here, a sodium haze there, an olive dusk somewhere
 * else. Replacing the *surface* palette with porcelain and graphite did not
 * remove any of that: the neutral materials were still being recoloured through
 * the light falling on them, the fog in front of them and the environment map
 * reflecting in them. A white wall under an olive sky is an olive wall.
 *
 * So the rendering palette is separated from the old thematic one entirely. A
 * map picks a lighting profile from a short curated list, all of them nearly
 * neutral and differing in *value* and direction rather than in hue, and picks
 * one accent. Those two choices plus the architectural language are the whole
 * of a map's visual identity, and every one of them is written down rather
 * than hashed out of the map's name.
 */

import * as THREE from 'three';

/**
 * The light a map is built under.
 *
 * Five, and they differ in the two things that actually change how
 * architecture reads: where the key comes from and how much of the scene is
 * not lit by it. The small warm/cool contrast within each is deliberate and
 * bounded — a shadow that is slightly blue under a slightly warm sun is how
 * daylight works, and it is nothing like a green sky.
 */
export type LightingProfile =
  | 'cool_overcast'
  | 'hard_morning'
  | 'warm_interior'
  | 'deep_interior'
  | 'blue_hour'
  | 'clinical_night';

export interface Lighting {
  /** Direction the key comes from, as a position to aim from. */
  sun: [number, number, number];
  sunColour: number;
  sunIntensity: number;
  /** Sky and ground terms, for the fill a room gets off its own surfaces. */
  skyColour: number;
  groundColour: number;
  hemiIntensity: number;
  /**
   * The one term a buried room can see.
   *
   * Much larger than it was, on every profile. A single key with a sky term
   * and almost no ambient is the right rig for an open map — every traced map
   * in this library is one — and it puts an enclosed band under a slab at
   * fifteen per cent of the range, which is a picture of a power cut. Three
   * quarters of the frame measured below the graphite threshold on every
   * authored map, including the walls.
   *
   * Ambient is unoccluded, so raising it lifts exactly the spaces that need it
   * and costs the open ones a little contrast. That is the correct trade for a
   * library that is about to be half sections.
   */
  ambient: number;
  /*
   * The background, and the fog that fades into it.
   *
   * Blue-grey, with the emphasis on grey: the sky is often the largest single
   * surface in an outdoor frame, so a sky with any real chroma in it stains a
   * fifth of the picture. It still reads as cool, because everything else is
   * neutral and cool is a comparison rather than a quantity.
   */
  horizon: number;
  zenith: number;
  fogNear: number;
  fogFar: number;
  /** Whether the key is hard enough to be worth a shadow map. */
  shadowed: boolean;
  /**
   * Whether this is after dark, which the map's own look cannot be asked.
   *
   * A traced map carries a `daylight` flag off its screenshot and the art
   * profile carries a lighting rig, and for some maps the two disagree: a
   * chlorine plant says daylight and is lit `clinical_night`. The rig is what
   * renders — `lightingOf` is what builds the scene's lights — so the rig is
   * what anything downstream has to ask.
   */
  night?: boolean;
}

export const LIGHTING: Record<LightingProfile, Lighting> = {
  /** Flat bright sky, weak direction. The kindest to bad geometry. */
  cool_overcast: {
    sun: [40, 120, 60], sunColour: 0xf2f6ff, sunIntensity: 1.5,
    skyColour: 0xdfe7f0, groundColour: 0xa3a3a3, hemiIntensity: 0.95, ambient: 0.30,
    horizon: 0xbfc4c9, zenith: 0xa2a9b0, fogNear: 34, fogFar: 190, shadowed: true,
  },
  /** Low sun, long shadows, the profile the style is really about. */
  hard_morning: {
    sun: [92, 66, 38], sunColour: 0xfff8f0, sunIntensity: 2.15,
    skyColour: 0xdde5ee, groundColour: 0x8f959e, hemiIntensity: 0.42, ambient: 0.25,
    // The horizon sits below the shell, not above it: a sky brighter than the
    // architecture puts the top of the frame at the ceiling of the range and
    // leaves the walls nothing to be lighter than.
    horizon: 0xc3c9cf, zenith: 0x94a0ab, fogNear: 42, fogFar: 220, shadowed: true,
  },
  /** Roof lights and no weather. Softer, and the shadows point down. */
  warm_interior: {
    sun: [26, 110, 30], sunColour: 0xfff5e9, sunIntensity: 2.1,
    skyColour: 0xe6e2dc, groundColour: 0x8f8a84, hemiIntensity: 0.6, ambient: 0.28,
    horizon: 0xcfcbc5, zenith: 0xb4b0aa, fogNear: 26, fogFar: 130, shadowed: true,
  },
  /**
   * Lit by the building, for the maps that are mostly inside one.
   *
   * The sixth profile, and the one this rig had no answer for. Every other
   * profile is a key plus a sky term, which is the right model for an open map
   * — and every traced map in this library is a single open storey, so it was
   * the only model ever needed. A three-band map is not that: two of its bands
   * are under a slab, they receive no key at all, and a sky term is a fraction
   * of the light a real interior has. The four authored maps came back with
   * three quarters of the frame below the graphite threshold and stayed there
   * through a brighter floor, a brighter ceiling, more ambient and weaker
   * occlusion, because none of those was the missing thing.
   *
   * What was missing is that a building lights itself. There is no local light
   * in this renderer and there should not be — a light per room is a shader
   * recompile per room — so the ambient term does the work of all of them at
   * once. It is large enough to be conspicuous in an open frame, which is why
   * this is a profile a map opts into rather than a change to the other five.
   */
  deep_interior: {
    sun: [30, 90, 45], sunColour: 0xfaf7f2, sunIntensity: 1.1,
    skyColour: 0xd7dade, groundColour: 0x8e9297, hemiIntensity: 1.15, ambient: 0.85,
    horizon: 0x9fa4aa, zenith: 0x7c8188, fogNear: 30, fogFar: 150, shadowed: false,
  },
  /**
   * After sundown: no key worth the name, everything from the sky.
   *
   * Much less blue than it was. The sky term is the *light*, not just the
   * backdrop — every white wall on the map is lit by it — and at 0x9fb2cc it
   * carried enough chroma to stain the whole shell. The sweep found five maps
   * where two thirds of the worst frame measured as weakly coloured, which is
   * precisely the failure the porcelain palette was adopted to end, arriving
   * through the light instead of through the paint. Cool is a comparison, not
   * a quantity: against neutral graphite and warm-neutral porcelain this still
   * reads as dusk at a fraction of the saturation.
   */
  blue_hour: {
    sun: [-70, 40, -50], sunColour: 0xdde2e9, sunIntensity: 1.3,
    skyColour: 0xc0c5cc, groundColour: 0x585b60, hemiIntensity: 0.85, ambient: 0.34,
    /*
     * Dusk, and not `night`, which I had it as for an afternoon.
     *
     * The reasoning was that a pier is lit blue hour and fails for having too
     * little pale in the frame, so it should get the lit fittings that fix a
     * night map. It does not. There is still sky in a blue hour, so the
     * fittings arrive on top of a frame that is already lit, and on the twelve
     * blue-hour maps they blew a chinatown to 29.9% of the frame clipped
     * against a limit of 22, took a dogtown's accent to 10.1% against 6 by
     * lighting its neon, and pushed a back alley — the palest frame in the
     * library — from 96.5% pale to 98.9%. What they bought in exchange was a
     * point or two of pale on three piers that are failing by twenty.
     *
     * Every map they do help is `clinical_night`. Fittings come on when the
     * sky goes out, not before.
     *
     * I came back and tried the smallest one of them here on its own — the
     * kerb light at the foot of a wall, which is where a dusk frame is dark.
     * It moved a calavera 58.7% dark to 57.8% and a lunacy 57.5% to 57.4%,
     * and took a back alley from 96.5% pale to 98.7%. A kerb is at the foot of
     * a wall and the dark in these frames is the middle of a road, which is
     * nowhere near one.
     */
    horizon: 0x9a9ea6, zenith: 0x62666e, fogNear: 30, fogFar: 150, shadowed: false,
  },
  /**
   * Night, and lit by the building rather than the sky.
   *
   * Substantially brighter than it was, because "dark" and "unreadable" are
   * not the same thing and the first version was the second. The sweep put
   * twelve maps on this profile and their worst frames came back at seven per
   * cent of the range — a black picture with a coral enemy somewhere in it,
   * which is not a night scene, it is a broken one.
   *
   * What makes a night read as night is not low absolute values, it is the
   * *contrast*: a dark sky against a lit building. So the sky and the fog stay
   * genuinely dark and the light on the architecture comes up to where the
   * shell is a mid value with the graphite still well below it. The threat
   * colour keeps all the room it had.
   */
  /*
   * Sixteen maps, and every `masses` failure in the library for having too
   * little *light* in the frame is one of them.
   *
   * A chlorine plant comes out 0.3% pale with 48% of the frame a porcelain
   * wall and 33% of it hardstanding. The wall faces away from the key and the
   * fill lifts it to somewhere around 0.45 — under the pale threshold, over
   * the dark one, part of neither mass.
   *
   * Taking the fill down and the key up does not fix it, which I tried:
   * 2.2/1.05/0.42 to 3.1/0.82/0.20, and on the twelve maps this profile
   * covers it moved `subject` not at all (11 failing either way), `masses`
   * not at all (8), and added three `spread` failures. The dark got darker
   * and nothing got lighter, because nothing in those frames faces the key
   * and no amount of key reaches a surface turned away from it.
   *
   * Eleven of those twelve fail something as they stand. What a night
   * exterior needs is a lit thing *in* it — a fitting, a window, a sign —
   * and that is a piece of work rather than a number here.
   */
  clinical_night: {
    sun: [50, 90, -40], sunColour: 0xebeef2, sunIntensity: 2.2,
    skyColour: 0xb4b8bd, groundColour: 0x46484c, hemiIntensity: 1.05, ambient: 0.42,
    horizon: 0x3a424e, zenith: 0x161c25, fogNear: 26, fogFar: 130, shadowed: false, night: true,
  },
};

/** The four accents, and a map gets exactly one. */
export type AccentName = 'cobalt' | 'jade' | 'amber' | 'cyan';

export const ACCENT_HEX: Record<AccentName, number> = {
  cobalt: 0x1d4ed8,
  jade: 0x0f9b74,
  amber: 0xd8a218,
  cyan: 0x11a8c4,
};

/**
 * What kind of building this is pretending to be.
 *
 * Drives which architectural hero a room is allowed — an excavation gets a
 * sunken court and a lightwell, a transit hall gets a bridge and a colonnade —
 * so the composition is chosen from a vocabulary that fits the place rather
 * than from whatever fixture the room's use table happened to name.
 */
export type Language =
  | 'excavation'
  | 'transit'
  | 'laboratory'
  | 'industrial'
  | 'civic'
  | 'rooftop';

export interface ArtProfile {
  accent: AccentName;
  lighting: LightingProfile;
  language: Language;
}

/**
 * The maps that have been art-directed, by hand.
 *
 * Everything not in here still falls back to a stable choice from the map's
 * name — a hundred and ten maps cannot all be directed at once — but the
 * fallback is explicitly the unfinished path, and a map being in this table is
 * what "this map has been looked at" means.
 */
export const PROFILES: Record<string, ArtProfile> = {
  // --- excavations, ruins and dig sites -------------------------------------
  //
  // Severe daylight from above, because that is what a dig has: no roof, hard
  // sun, and voids in the ground that read as black. The hero is a lightwell,
  // which is the same idea indoors.
  ancient: { accent: 'cobalt', lighting: 'hard_morning', language: 'excavation' },
  anubis: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },
  aztec: { accent: 'jade', lighting: 'warm_interior', language: 'excavation' },
  dust: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },
  dust_ii: { accent: 'cobalt', lighting: 'hard_morning', language: 'excavation' },
  dust_ii_control: { accent: 'cobalt', lighting: 'hard_morning', language: 'excavation' },
  dust2: { accent: 'cobalt', lighting: 'hard_morning', language: 'excavation' },
  mirage: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },
  gwalior: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },
  thera_lower: { accent: 'cyan', lighting: 'hard_morning', language: 'excavation' },
  thera_upper: { accent: 'cyan', lighting: 'cool_overcast', language: 'excavation' },
  crete: { accent: 'cobalt', lighting: 'hard_morning', language: 'excavation' },
  basalt: { accent: 'amber', lighting: 'blue_hour', language: 'excavation' },
  ruins: { accent: 'jade', lighting: 'cool_overcast', language: 'excavation' },
  sugarcane: { accent: 'jade', lighting: 'warm_interior', language: 'excavation' },
  calavera: { accent: 'amber', lighting: 'blue_hour', language: 'excavation' },
  mikla: { accent: 'cyan', lighting: 'cool_overcast', language: 'excavation' },
  jura: { accent: 'jade', lighting: 'cool_overcast', language: 'excavation' },
  grail: { accent: 'amber', lighting: 'warm_interior', language: 'excavation' },
  monastery: { accent: 'jade', lighting: 'hard_morning', language: 'excavation' },
  st_marc: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },

  // --- flood control -------------------------------------------------------
  //
  // A quarter built around an automated pumping and floodgate system, drawn
  // from its own cross-section rather than from a floorplan: a public
  // promenade above, a drained reservoir below it, a maintenance culvert
  // below that, and an inspection run over the top of all three. The accent
  // is cobalt and it belongs to the gate.
  floodworks: { accent: 'cobalt', lighting: 'deep_interior', language: 'industrial' },

  // --- the generated originals ---------------------------------------------
  //
  // Four places the library did not have: a station, a production line,
  // something you descend into, and housing stacked round a void. Each was
  // written as a paragraph and a section and laid out forty times by the
  // slicer; these are the four that kept what the paragraph claimed.
  terminus: { accent: 'jade', lighting: 'deep_interior', language: 'transit' },
  foundry: { accent: 'amber', lighting: 'deep_interior', language: 'industrial' },
  // Lit rather than dark: a buried hall has no sky to be dim under, so the
  // clinical profile put seventy per cent of the frame below the graphite
  // threshold and the map read as a power cut rather than as a basement.
  cistern: { accent: 'cyan', lighting: 'warm_interior', language: 'excavation' },
  arcology: { accent: 'cobalt', lighting: 'deep_interior', language: 'civic' },
  sorting: { accent: 'amber', lighting: 'deep_interior', language: 'industrial' },
  hangar: { accent: 'cobalt', lighting: 'cool_overcast', language: 'industrial' },
  coldstore: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  nightmarket: { accent: 'amber', lighting: 'warm_interior', language: 'civic' },
  archive: { accent: 'cobalt', lighting: 'deep_interior', language: 'civic' },
  switchyard: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  scaffold: { accent: 'amber', lighting: 'cool_overcast', language: 'rooftop' },
  quarry: { accent: 'amber', lighting: 'cool_overcast', language: 'excavation' },
  relay: { accent: 'cyan', lighting: 'blue_hour', language: 'laboratory' },
  drydock: { accent: 'jade', lighting: 'cool_overcast', language: 'transit' },

  // --- the generated originals, batch two ----------------------------------
  //
  // Thirty-six more places, chosen so that no two of them have the same
  // section: a radial shed, three identical stacked floors, a tube, a
  // diagonal, a ring, a hillside that steps outward, a building that is
  // mostly solid. Each ships as up to three layouts, and a variant shares its
  // concept's look because it is the same place with the walls moved.
  courthouse: { accent: 'cobalt', lighting: 'deep_interior', language: 'civic' },
  infirmary: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  observatory: { accent: 'cobalt', lighting: 'clinical_night', language: 'laboratory' },
  nave: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  bathhouse: { accent: 'jade', lighting: 'warm_interior', language: 'civic' },
  assembly: { accent: 'cobalt', lighting: 'deep_interior', language: 'civic' },
  school: { accent: 'jade', lighting: 'cool_overcast', language: 'civic' },
  stacks: { accent: 'amber', lighting: 'deep_interior', language: 'laboratory' },
  reserve: { accent: 'cyan', lighting: 'deep_interior', language: 'civic' },
  exchange: { accent: 'jade', lighting: 'deep_interior', language: 'laboratory' },
  mint: { accent: 'amber', lighting: 'deep_interior', language: 'industrial' },
  committal: { accent: 'cobalt', lighting: 'warm_interior', language: 'civic' },

  roundhouse: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  grain: { accent: 'amber', lighting: 'cool_overcast', language: 'industrial' },
  mill: { accent: 'jade', lighting: 'deep_interior', language: 'industrial' },
  chillworks: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  brickworks: { accent: 'amber', lighting: 'hard_morning', language: 'excavation' },
  gasworks: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  waterworks: { accent: 'cobalt', lighting: 'deep_interior', language: 'excavation' },
  depot: { accent: 'amber', lighting: 'warm_interior', language: 'transit' },
  containers: { accent: 'cobalt', lighting: 'cool_overcast', language: 'transit' },
  funicular: { accent: 'jade', lighting: 'hard_morning', language: 'transit' },
  lock: { accent: 'cyan', lighting: 'cool_overcast', language: 'industrial' },
  bunkering: { accent: 'amber', lighting: 'blue_hour', language: 'industrial' },

  tunnel: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  blockhouse: { accent: 'jade', lighting: 'clinical_night', language: 'excavation' },
  silo: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  radar: { accent: 'cobalt', lighting: 'blue_hour', language: 'rooftop' },
  barracks: { accent: 'jade', lighting: 'hard_morning', language: 'civic' },
  cabletop: { accent: 'cobalt', lighting: 'cool_overcast', language: 'rooftop' },
  growhouse: { accent: 'jade', lighting: 'deep_interior', language: 'laboratory' },
  datahall: { accent: 'cyan', lighting: 'deep_interior', language: 'laboratory' },
  broadcast: { accent: 'amber', lighting: 'deep_interior', language: 'civic' },
  pier: { accent: 'cyan', lighting: 'blue_hour', language: 'transit' },
  terraces: { accent: 'jade', lighting: 'hard_morning', language: 'excavation' },
  refuge: { accent: 'amber', lighting: 'warm_interior', language: 'civic' },

  // --- transit: rails, roads, terminals ------------------------------------
  //
  // Flat overcast light, so the long horizontal spans read as spans rather
  // than as shadows. The hero is a bridge, which is what these places are.
  interchange: { accent: 'jade', lighting: 'cool_overcast', language: 'transit' },
  train: { accent: 'cobalt', lighting: 'cool_overcast', language: 'transit' },
  train_lower: { accent: 'cobalt', lighting: 'clinical_night', language: 'transit' },
  train_upper: { accent: 'cobalt', lighting: 'cool_overcast', language: 'transit' },
  shorttrain: { accent: 'cobalt', lighting: 'blue_hour', language: 'transit' },
  rails: { accent: 'amber', lighting: 'cool_overcast', language: 'transit' },
  overpass: { accent: 'jade', lighting: 'cool_overcast', language: 'transit' },
  baggage_lower: { accent: 'cyan', lighting: 'warm_interior', language: 'transit' },
  baggage_upper: { accent: 'cyan', lighting: 'cool_overcast', language: 'transit' },
  cruise: { accent: 'cobalt', lighting: 'hard_morning', language: 'transit' },
  seaside: { accent: 'cobalt', lighting: 'hard_morning', language: 'transit' },
  coast: { accent: 'cyan', lighting: 'cool_overcast', language: 'transit' },
  pitstop_lower: { accent: 'amber', lighting: 'warm_interior', language: 'transit' },
  pitstop_upper: { accent: 'amber', lighting: 'cool_overcast', language: 'transit' },
  grind: { accent: 'cyan', lighting: 'blue_hour', language: 'transit' },
  thrill: { accent: 'amber', lighting: 'blue_hour', language: 'transit' },
  dizzy: { accent: 'cyan', lighting: 'cool_overcast', language: 'transit' },
  climb_lower: { accent: 'jade', lighting: 'warm_interior', language: 'transit' },
  climb_upper: { accent: 'jade', lighting: 'hard_morning', language: 'transit' },

  // --- laboratory and containment ------------------------------------------
  //
  // Night, because these are the places that are lit by themselves — and the
  // hero is a louvred wall, which is the one piece of architecture in the set
  // that turns a flat plane into forty alternating ones.
  nuke: { accent: 'cyan', lighting: 'clinical_night', language: 'laboratory' },
  nuke_lower: { accent: 'cyan', lighting: 'clinical_night', language: 'laboratory' },
  nuke_upper: { accent: 'cyan', lighting: 'cool_overcast', language: 'laboratory' },
  subzero: { accent: 'cyan', lighting: 'clinical_night', language: 'laboratory' },
  chlorine: { accent: 'jade', lighting: 'clinical_night', language: 'laboratory' },
  poseidon: { accent: 'cyan', lighting: 'clinical_night', language: 'laboratory' },
  hive: { accent: 'jade', lighting: 'clinical_night', language: 'laboratory' },
  studio: { accent: 'cyan', lighting: 'warm_interior', language: 'laboratory' },
  museum: { accent: 'cobalt', lighting: 'warm_interior', language: 'laboratory' },
  guard: { accent: 'cyan', lighting: 'clinical_night', language: 'laboratory' },
  extraction: { accent: 'amber', lighting: 'clinical_night', language: 'laboratory' },
  lunacy: { accent: 'cyan', lighting: 'blue_hour', language: 'laboratory' },
  phoenix_compound: { accent: 'amber', lighting: 'clinical_night', language: 'laboratory' },
  safehouse: { accent: 'amber', lighting: 'clinical_night', language: 'laboratory' },

  // --- industrial process --------------------------------------------------
  //
  // Roof lights and no weather. A mass hangs in the volume, because the volume
  // above the fight is the thing these buildings actually have.
  brewery: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  cache: { accent: 'amber', lighting: 'cool_overcast', language: 'industrial' },
  assault: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  boyard: { accent: 'jade', lighting: 'warm_interior', language: 'industrial' },
  engage: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  militia_lower: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  militia_upper: { accent: 'amber', lighting: 'cool_overcast', language: 'industrial' },
  motel: { accent: 'amber', lighting: 'blue_hour', language: 'industrial' },
  mutiny: { accent: 'jade', lighting: 'hard_morning', language: 'industrial' },
  pool_day: { accent: 'cyan', lighting: 'warm_interior', language: 'industrial' },
  resort: { accent: 'jade', lighting: 'warm_interior', language: 'industrial' },
  siege: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  stronghold: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  warden: { accent: 'cobalt', lighting: 'warm_interior', language: 'industrial' },
  whistle: { accent: 'amber', lighting: 'cool_overcast', language: 'industrial' },
  workout: { accent: 'jade', lighting: 'warm_interior', language: 'industrial' },
  lake: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  chalice: { accent: 'amber', lighting: 'warm_interior', language: 'industrial' },
  swamp: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  overgrown: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  biome: { accent: 'jade', lighting: 'warm_interior', language: 'industrial' },
  zoo: { accent: 'jade', lighting: 'cool_overcast', language: 'industrial' },
  ravine: { accent: 'jade', lighting: 'hard_morning', language: 'industrial' },
  season: { accent: 'cyan', lighting: 'cool_overcast', language: 'industrial' },

  // --- civic, commercial and domestic --------------------------------------
  //
  // Overcast or low sun, and a reflecting pool on the floor of the largest
  // room — the one composition here that needs no height at all, and the one
  // that gives a white hall a black rectangle to be white against.
  office: { accent: 'cyan', lighting: 'cool_overcast', language: 'civic' },
  kasbah: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  inferno: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  italy: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  abbey: { accent: 'jade', lighting: 'cool_overcast', language: 'civic' },
  agency: { accent: 'cyan', lighting: 'cool_overcast', language: 'civic' },
  ali: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  apollo: { accent: 'cobalt', lighting: 'cool_overcast', language: 'civic' },
  atrium: { accent: 'cyan', lighting: 'warm_interior', language: 'civic' },
  austria: { accent: 'jade', lighting: 'cool_overcast', language: 'civic' },
  backalley: { accent: 'amber', lighting: 'blue_hour', language: 'civic' },
  bank: { accent: 'cyan', lighting: 'warm_interior', language: 'civic' },
  blagai: { accent: 'jade', lighting: 'hard_morning', language: 'civic' },
  castle: { accent: 'cobalt', lighting: 'cool_overcast', language: 'civic' },
  chinatown: { accent: 'amber', lighting: 'blue_hour', language: 'civic' },
  cobblestone: { accent: 'jade', lighting: 'cool_overcast', language: 'civic' },
  dogtown: { accent: 'cyan', lighting: 'blue_hour', language: 'civic' },
  downtown: { accent: 'cobalt', lighting: 'cool_overcast', language: 'civic' },
  edin: { accent: 'jade', lighting: 'cool_overcast', language: 'civic' },
  elysion: { accent: 'cobalt', lighting: 'hard_morning', language: 'civic' },
  empire: { accent: 'cobalt', lighting: 'blue_hour', language: 'civic' },
  favela: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  golden: { accent: 'amber', lighting: 'warm_interior', language: 'civic' },
  iris: { accent: 'cyan', lighting: 'cool_overcast', language: 'civic' },
  lite: { accent: 'cobalt', lighting: 'cool_overcast', language: 'civic' },
  marquis: { accent: 'jade', lighting: 'warm_interior', language: 'civic' },
  mocha: { accent: 'amber', lighting: 'warm_interior', language: 'civic' },
  palacio: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },
  palais_lower: { accent: 'cobalt', lighting: 'warm_interior', language: 'civic' },
  palais_upper: { accent: 'cobalt', lighting: 'cool_overcast', language: 'civic' },
  rialto: { accent: 'cyan', lighting: 'hard_morning', language: 'civic' },
  royal: { accent: 'cobalt', lighting: 'warm_interior', language: 'civic' },
  ruby: { accent: 'amber', lighting: 'blue_hour', language: 'civic' },
  sanctum: { accent: 'jade', lighting: 'warm_interior', language: 'civic' },
  tuscan: { accent: 'amber', lighting: 'hard_morning', language: 'civic' },

  // --- rooftops and anything you look down from ----------------------------
  //
  // A cantilever: an upper floor pushed out over the space, with brackets
  // under it. On a rooftop it is the only thing that says the building
  // continues, which is what a rooftop map is about.
  okujou: { accent: 'cobalt', lighting: 'clinical_night', language: 'rooftop' },
  // The tower: three floors stacked in one column, an atrium cut through them
  // and a plant deck on top. Overcast rather than the dusk it was first given:
  // most of what the player sees is the *inside* of an office, and an interior
  // at blue hour is an interior with the lights off.
  riser: { accent: 'cyan', lighting: 'cool_overcast', language: 'rooftop' },
  rooftop: { accent: 'cobalt', lighting: 'blue_hour', language: 'rooftop' },
  vertigo_lower: { accent: 'amber', lighting: 'cool_overcast', language: 'rooftop' },
  vertigo_upper: { accent: 'amber', lighting: 'hard_morning', language: 'rooftop' },
};

const FALLBACK_ACCENTS: AccentName[] = ['cobalt', 'jade', 'amber', 'cyan'];
const FALLBACK_LIGHT: LightingProfile[] = ['cool_overcast', 'hard_morning', 'warm_interior', 'blue_hour'];
const FALLBACK_LANGUAGE: Language[] = ['civic', 'industrial', 'transit', 'laboratory'];

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * The name a map is filed under, whatever the plan called it.
 *
 * Every plan's own header carries a source prefix — `cs_` for the traced
 * ones, `og_` for the originals, `nt_` for the one port — and the runtime
 * reads the spec's id, while the library and this table both key off the
 * filename. So `profileFor` was being handed `cs_ancient` and matching
 * nothing, on every map, for the whole library: a hundred and twenty-two
 * hand-written art profiles that had never once been used.
 *
 * Nothing failed loudly. The fallback hash returns a perfectly plausible
 * profile, so every map had a light, an accent and a language — just not the
 * ones anybody chose.
 */
function filed(id: string): string {
  const named = /^(cs|og|nt)_/.test(id) ? id.slice(3) : id;
  /*
   * And a variant looks like the place it is a variant of.
   *
   * A concept can be laid out several ways and each layout ships as its own
   * map — `terminus`, `terminus_b`, `terminus_c` — but they are one place with
   * the walls in different positions, so they share a light, an accent and a
   * kind of building. Which is also what makes the run order's rule against
   * two consecutive floors with the same look do the right thing with them:
   * it keeps two cuts of one station apart.
   */
  return named.replace(/_[b-e]$/, '');
}

export function profileFor(id: string): ArtProfile {
  const named = PROFILES[id] ?? PROFILES[filed(id)];
  if (named) return named;
  const h = hash(id);
  return {
    accent: FALLBACK_ACCENTS[h % FALLBACK_ACCENTS.length],
    lighting: FALLBACK_LIGHT[(h >>> 8) % FALLBACK_LIGHT.length],
    language: FALLBACK_LANGUAGE[(h >>> 16) % FALLBACK_LANGUAGE.length],
  };
}

/** Whether this map's look was decided by a person or by a hash. */
export function isDirected(id: string): boolean {
  return id in PROFILES || filed(id) in PROFILES;
}

export function accentOf(id: string): number {
  return ACCENT_HEX[profileFor(id).accent];
}

export function lightingOf(id: string): Lighting {
  return LIGHTING[profileFor(id).lighting];
}

/**
 * A tinted colour, mostly neutralised, keeping its lightness.
 *
 * Used only where a value still has to come from somewhere outside this file.
 * Anything genuinely art-directed should be a literal in `LIGHTING` instead.
 */
export function calm(hex: number, keep: number): THREE.Color {
  const c = new THREE.Color(hex);
  const grey = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  return c.lerp(new THREE.Color(grey, grey, grey), 1 - keep);
}
